import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import AnimatedContainer from 'ember-animated/components/animated-container';
import animatedIf from 'ember-animated/components/animated-if';
import fade from 'ember-animated/transitions/fade';

import UiWellItem from './item';

export interface UiWellSectionSignature<T> {
  Args: {
    /**
     * Control section expanded or collapsed state externally
     */
    isOpen?: boolean;

    /**
     * Array to yield in <:items>. Each item will be pre-wrapped in a WellItem
     */
    items: T[];

    /**
     * Section title. Clickable to expand/collapse by default. For basic
     * title, use <:title> named block and pass in your own header.
     */
    title: string;
  };
  Blocks: {
    default?: [];
    items?: [T];
    title?: [];
  };
  Element: HTMLElement; // TS doesn't have an `HTMLSectionElement` type
}

export default class UiWellSection<T> extends Component<UiWellSectionSignature<T>> {
  @tracked _isOpen = true;

  get isOpen(): boolean {
    return this.args.isOpen ?? this._isOpen;
  }

  @action
  toggleSection(): void {
    this._isOpen = !this.isOpen;
  }

  <template>
    <section class="ui__well__section" ...attributes>
      {{#if (has-block "title")}}
        {{yield to="title"}}
      {{else}}
        <h4 class="cursor-pointer" role="button" {{on "click" this.toggleSection}}>
          {{@title}}
          <span class="carot {{if this.isOpen 'sort-desc' 'sort-asc'}}" type="button"></span>
        </h4>
      {{/if}}

      <AnimatedContainer>
        {{#animatedIf this.isOpen use=fade duration=200}}
          {{#if (has-block "items")}}
            {{#each @items as |item|}}
              <UiWellItem>
                {{yield item to="items"}}
              </UiWellItem>
            {{/each}}
          {{else}}
            {{yield}}
          {{/if}}
        {{/animatedIf}}
      </AnimatedContainer>
    </section>
  </template>
}
